import React, {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Button, Card } from "react-bootstrap";
import { Loading } from "../../ui";
import { AdminTable } from "../admin/AdminTable";
import {
	EditableTextCell,
	SwitchCell,
	SwitchFilter,
	switchFilter,
} from "../admin/cells";
import { useFlash } from "../admin/Flash";
import { useSavedRows } from "../admin/useSavedRows";
import { ProductsNav } from "../ProductsNav";
import { Library } from "./Library";
import { LibraryForm } from "./LibraryForm";
import { ILibraryPatch, LibraryService } from "./LibraryService";

const libraryService = new LibraryService();

function toPatch(changes: Partial<Library>): ILibraryPatch {
	const { isElearning, ...fields } = changes;
	return isElearning === undefined
		? fields
		: { ...fields, is_elearning: isElearning };
}

export const LibraryPage = (): ReactElement => {
	const [showCreate, setShowCreate] = useState(false);
	const flash = useFlash();
	const { rows: libraries, setRows, statuses, save } = useSavedRows<Library>(
		message => flash.show(`Couldn't save: ${message}`, true),
	);

	useEffect(() => {
		libraryService.findAll().then(setRows);
	}, []);

	const saveRow = useCallback(
		(id: number, changes: Partial<Library>) =>
			save([id], changes, async () => [
				await libraryService.patch(id, toPatch(changes)),
			]),
		[save],
	);

	const columns = useMemo(
		() => [
			{ Header: "ID", accessor: "id", width: "6%", disableFilters: true },
			{
				Header: "Name",
				accessor: "name",
				width: "50%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "Code",
				accessor: "code",
				width: "15%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "eLearning",
				accessor: "isElearning",
				width: "29%",
				Cell: SwitchCell,
				Filter: SwitchFilter,
				filter: switchFilter,
				onLabel: "eLearning",
				offLabel: "Not eLearning",
			},
		],
		[],
	);

	if (!libraries) {
		return <Loading />;
	}

	const elearningCount = libraries.filter(l => l.isElearning).length;

	return (
		<div className="container mb-3" id="products-admin">
			<div className="products-page-header">
				<div className="products-page-header-left">
					<div className="products-page-icon products-page-icon-libraries">
						<i className="fa fa-book" />
					</div>
					<div>
						<h1>Libraries</h1>
						<span className="products-page-count">
							{libraries.length} librar
							{libraries.length !== 1 ? "ies" : "y"}
							{elearningCount > 0 && (
								<> &middot; {elearningCount} eLearning</>
							)}
						</span>
					</div>
				</div>
				<Button
					variant={showCreate ? "outline-secondary" : "primary"}
					onClick={() => setShowCreate(!showCreate)}
					className="products-create-toggle"
				>
					<i
						className={`fa ${showCreate ? "fa-times" : "fa-plus"}`}
					/>{" "}
					{showCreate ? "Cancel" : "New Library"}
				</Button>
			</div>

			<ProductsNav active="libraries" />

			{flash.element}

			{showCreate && (
				<Card className="mb-4 products-create-card">
					<Card.Body>
						<LibraryForm
							onCreate={library => {
								setRows([library, ...libraries]);
								setShowCreate(false);
								flash.show(`"${library.name}" created`);
							}}
						/>
					</Card.Body>
				</Card>
			)}

			<div className="editable-data-table products-table-wrapper">
				<AdminTable
					columns={columns}
					data={libraries}
					statuses={statuses}
					saveRow={saveRow}
				/>
			</div>
		</div>
	);
};
